import { HStack } from '@zealot/ui/components/hstack';
import { Text } from '@zealot/ui/components/text';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const statusPillVariants = cva('rounded-full', {
  variants: {
    tone: {
      success: 'bg-success-muted',
      warning: 'bg-warning-muted',
      info: 'bg-info-muted',
      destructive: 'bg-destructive-muted',
    },
    size: {
      sm: 'px-2 py-0.5',
      md: 'px-3 py-1',
    },
  },
  defaultVariants: { tone: 'success', size: 'md' },
});

const statusPillDotVariants = cva('size-2 shrink-0 rounded-full', {
  variants: {
    tone: {
      success: 'bg-success',
      warning: 'bg-warning',
      info: 'bg-info',
      destructive: 'bg-destructive',
    },
  },
  defaultVariants: { tone: 'success' },
});

const PILL_TEXT_SIZE = { sm: 'xs', md: 'sm' } as const;

// gloss
type StatusPillProps = Omit<React.ComponentProps<'div'>, 'children'> &
  VariantProps<typeof statusPillVariants> & {
    label: React.ReactNode;
    showDot?: boolean;
    toneClassName?: string;
  };

// gloss
const StatusPill = ({
  className,
  tone = 'success',
  size = 'md',
  label,
  showDot = true,
  toneClassName,
  ...props
}: StatusPillProps) => {
  const isToneOverridden = Boolean(toneClassName);
  const resolvedSize = size ?? 'md';

  return (
    <HStack
      gap={2}
      data-slot="status-pill"
      className={cn(statusPillVariants({ tone, size: resolvedSize }), toneClassName, className)}
      {...props}
    >
      {showDot && (
        <span
          aria-hidden
          data-slot="status-pill-dot"
          className={cn(statusPillDotVariants({ tone }), isToneOverridden && 'bg-current')}
        />
      )}
      <Text
        size={PILL_TEXT_SIZE[resolvedSize]}
        weight="semibold"
        tone={tone}
        className={cn('whitespace-nowrap', isToneOverridden && 'text-inherit')}
      >
        {label}
      </Text>
    </HStack>
  );
};

export type { StatusPillProps };
export { StatusPill, statusPillVariants };
